/**
 * Formatação de data e hora.
 *
 * O fuso é **fixado em São Paulo**, não herdado da máquina: um agendamento das
 * 08:00 na clínica precisa aparecer como 08:00 no navegador de quem viaja, no
 * servidor que roda em UTC e no teste. Sem `timeZone`, o mesmo dado vira 11:00
 * numa máquina em UTC — e ninguém percebe até o paciente perder a consulta.
 */

const FUSO = 'America/Sao_Paulo';

export function horaDe(iso: string): string {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: FUSO });
}

export function dataPorExtenso(data: Date = new Date()): string {
    return data.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: FUSO });
}

export function diaIso(data: Date = new Date()): string {
    /* `en-CA` porque o formato dele é exatamente YYYY-MM-DD — e converter com
       toISOString() daria o dia em UTC, que depois das 21h já é o dia seguinte. */
    return data.toLocaleDateString('en-CA', { timeZone: FUSO });
}

export function saudacao(data: Date = new Date()): string {
    const hora = Number(data.toLocaleString('pt-BR', { hour: '2-digit', hour12: false, timeZone: FUSO }));
    if (hora < 12) return 'Bom dia';
    if (hora < 18) return 'Boa tarde';
    return 'Boa noite';
}

/** "2026-09-08" e "14:30" viram "08/09 às 14:30", como o aviso mostra. */
export function dataEHoraCurtas(dataIso: string, hora: string): string {
    const [, mes, dia] = dataIso.split('-');
    return `${dia}/${mes} às ${hora}`;
}

export interface DiaDaGrade {
    /** `YYYY-MM-DD`. */
    iso: string;
    /** "seg", "ter"… */
    rotuloCurto: string;
    /** Número do dia no mês. */
    numero: number;
    ehHoje: boolean;
    ehFimDeSemana: boolean;
}

/** Constrói um `Date` ao meio-dia do fuso da clínica.
 *  Meia-noite encosta na virada do dia e, num fuso a oeste, cai no dia anterior. */
export function dataDe(diaIsoStr: string): Date {
    return new Date(`${diaIsoStr}T12:00:00-03:00`);
}

export function somarDias(diaIsoStr: string, dias: number): string {
    const d = dataDe(diaIsoStr);
    d.setDate(d.getDate() + dias);
    return diaIso(d);
}

/** Segunda-feira da semana que contém o dia. */
export function segundaDaSemana(diaIsoStr: string): string {
    const d = dataDe(diaIsoStr);
    /* getDay() 0 é domingo. A semana da clínica começa na segunda e o sábado
       ainda pertence a ela, então o domingo recua seis dias, não zero. */
    return somarDias(diaIsoStr, -((d.getDay() + 6) % 7));
}

export function diasDaGrade(inicioIso: string, quantidade: number, hojeIso = diaIso()): DiaDaGrade[] {
    return Array.from({ length: quantidade }, (_, i) => {
        const iso = somarDias(inicioIso, i);
        const d = dataDe(iso);
        const semana = d.getDay();
        return {
            iso,
            rotuloCurto: d.toLocaleDateString('pt-BR', { weekday: 'short', timeZone: FUSO }).replace('.', ''),
            numero: Number(iso.slice(8, 10)),
            ehHoje: iso === hojeIso,
            ehFimDeSemana: semana === 0 || semana === 6,
        };
    });
}

export function mesEAno(diaIsoStr: string): string {
    return dataDe(diaIsoStr).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric', timeZone: FUSO });
}

/** Hora do instante como número fracionário: 14:30 vira 14,5. */
export function horaFracionaria(iso: string): number {
    const [hora = '0', minuto = '0'] = horaDe(iso).split(':');
    return Number(hora) + Number(minuto) / 60;
}

export function dataCurta(iso: string): string {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: FUSO });
}

/** "08/09/2026 · 09:14", como o histórico mostra. */
export function dataEHoraPorExtenso(iso: string): string {
    return `${dataCurta(iso)} · ${horaDe(iso)}`;
}

/** Centavos para "R$ 4.350,00". O dado é inteiro; o cifrão é da tela. */
export function moeda(centavos: number): string {
    return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** "1992-03-12" vira "12/03/1992". Sem hora, sem fuso: é data de calendário. */
export function dataSimples(diaIsoStr: string | undefined): string {
    if (!diaIsoStr) return '—';
    const [ano, mes, dia] = diaIsoStr.split('-');
    return dia && mes && ano ? `${dia}/${mes}/${ano}` : '—';
}

/** Dias inteiros entre dois dias de calendário, sem passar por fuso. */
export function diasEntre(deIso: string, ateIso: string): number {
    return Math.round((dataDe(ateIso).getTime() - dataDe(deIso).getTime()) / 86_400_000);
}

/**
 * "10/09/2026", "Hoje", "Amanhã" ou "Atrasado 2d".
 *
 * O atraso é calculado, não gravado: no protótipo a string "Atrasado 2d" estava
 * no dado e continuava dizendo dois dias para sempre.
 */
export function proximoContato(diaIsoStr: string | undefined, hojeIso = diaIso()): string {
    if (!diaIsoStr) return '—';
    const diferenca = diasEntre(hojeIso, diaIsoStr);
    if (diferenca < 0) return `Atrasado ${Math.abs(diferenca)}d`;
    if (diferenca === 0) return 'Hoje';
    if (diferenca === 1) return 'Amanhã';
    return dataSimples(diaIsoStr);
}

/** "34 anos" a partir da data de nascimento. */
export function idadeEm(nascimentoIso: string, hojeIso = diaIso()): string {
    if (!nascimentoIso) return '—';
    const [ano = '0', mes = '0', dia = '0'] = nascimentoIso.split('-');
    const [anoHoje = '0', mesHoje = '0', diaHoje = '0'] = hojeIso.split('-');
    let idade = Number(anoHoje) - Number(ano);
    /* Aniversário que ainda não chegou este ano tira um: sem isso, quem nasceu
       em dezembro fica um ano mais velho durante onze meses. */
    if (Number(mesHoje) < Number(mes) || (mesHoje === mes && Number(diaHoje) < Number(dia))) idade -= 1;
    return `${idade} anos`;
}
